import { ChangeDetectorRef, Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonIcon, IonLabel } from '@ionic/angular/standalone';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';

import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { Roles } from '@app/shared/utils/roles.utils';
import { MenuOption as RouteModel } from '@app/core/models/route.model';

import { GsapRevealDirective } from '@app/shared/directives/gsap-reveal.directive';
import { LogoComponent } from '../logo/logo.component';
import { RippleDirective } from '@app/shared/directives/ripple.directive';
import { TiltHoverDirective } from '@app/shared/directives/tilt-hover.directive';

declare var FlowbiteInstances: any;

@Component({
	selector: 'app-sidebar',
	standalone: true,
	imports: [
		CommonModule,
		RouterLink,
		RouterLinkActive,
		IonLabel,
		IonIcon,
		GsapRevealDirective,
		RippleDirective,
		TiltHoverDirective,
		LogoComponent,
	],
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
	private optionsUnsubscribe = new Subject<void>();
	options: RouteModel[] = [];
	roles_enums = Roles;

	public role: number = 0;

	constructor(
		private _router: Router,
		private _authService: AuthenticationService,
		private changeDetectorRef: ChangeDetectorRef,
	) {}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		if (window.innerWidth < 960) { // md breakpoint is 960px
			const sidebar = document.getElementById('sidebar');
			const toggleButton = document.querySelector('[data-drawer-toggle="sidebar"]');
			const target = event.target as HTMLElement;

			// If sidebar is open (doesn't have -translate-x-full) and click is outside
			if (sidebar && !sidebar.classList.contains('-translate-x-full')) {
				if (!sidebar.contains(target) && toggleButton && !toggleButton.contains(target)) {
					this.closeDrawer();
				}
			}
		}
	}

	ngOnInit(): void {
		this.getOptions();
		this.subscribeEventToReloadMenu();
		this.role = parseInt(this._authService.currentUser.role);
		this.closeSidebarOnNavigation();
	}

	private subscribeEventToReloadMenu() {
		this._authService.reloadMenu$.subscribe({
			next: () => {
				this.getOptions();
			},
		});
	}

	private closeSidebarOnNavigation() {
		this._router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			takeUntil(this.optionsUnsubscribe)
		).subscribe(() => {
			this.closeDrawer();
		});
	}

	public closeDrawer() {
		// Use native Flowbite instance if available
		try {
			if (typeof FlowbiteInstances !== 'undefined') {
				const drawer = FlowbiteInstances.getInstance('Drawer', 'sidebar');
				if (drawer) {
					drawer.hide();
					return;
				}
			}
		} catch (e) {
			console.warn('FlowbiteInstances error:', e);
		}

		// Fallback: Dispatch a click to any element with data-drawer-hide="sidebar"
		// or manually manipulate classes if Flowbite instance is not reachable
		const closeButtons = document.querySelectorAll('[data-drawer-hide="sidebar"]');
		if (closeButtons.length > 0) {
			(closeButtons[0] as HTMLElement).click();
		} else {
			const sidebar = document.getElementById('sidebar');
			if (sidebar) {
				sidebar.classList.add('-translate-x-full');
				sidebar.classList.remove('translate-x-0');
			}
		}
	}

	getOptions() {
		this._authService
			.getMenu()
			.pipe(takeUntil(this.optionsUnsubscribe))
			.subscribe((response) => {
				this.options = response.map(
					(a) => new RouteModel(a.id, a.name, a.route, a.icon, a.childs, false),
				);
				this.changeDetectorRef.detectChanges();
			});
	}

	ngOnDestroy(): void {
		this.optionsUnsubscribe.next();
		this.optionsUnsubscribe.complete();
		this.optionsUnsubscribe.unsubscribe();
	}

	async logOut() {
		const token = await this._authService.getFcmToken();
		this._authService.signOut(token).subscribe({
			next: () => {
				localStorage.removeItem('access_token');
				this._router.navigateByUrl('/auth/login');
			},
			error: () => {
				localStorage.removeItem('access_token');
				this._router.navigateByUrl('/auth/login');
			},
		});
	}
}
